import { useEffect, useState } from "react"
import { AxiosError, AxiosResponse } from "axios"
import useApp from "../../hooks/useApp"
import useLink from "../../hooks/useLink"
import { getLink } from "../../services/link.api"
import { IPaymentFormConfiguration, transformLink } from "../../services/link.transform"
import { postCharges, postEmailReceipt, getCharges, getChargeByClientId, getChargeBody, getEmailReceiptBody } from "../../services/charges.api"
import { transformGetCharge, transformPostCharge } from "../../services/charges.transform"
import { STORAGE_CHARGE_ID, STORAGE_LANGUAGE } from "../../helpers/constants"
import { CallbackUriPath, ErrorResponse, LinkStatus, OmiseChargeStatus, PaymentInput, Progress } from "../../helpers/enums"
import { getItem, removeItem, setItem } from "../../helpers/storage"
import {
  getCallbackUriType,
  getLinkId,
  getPayAmount,
  getPaylinkUri,
  Psp,
  redirect,
  updateFavicon
} from "../../helpers/utils"

import Paylink from "../Assemble/Paylink"

import { OmiseCallbackPayload } from "../../plugins/omisejs-wrapped/components/App"
import InvalidLink from "../Assemble/InvalidLink"
import { useAnalytics, events } from '../../helpers/analytics'


const RETRY_COUNT=15
const RETRY_TIME=1000

const Index = () => {
  const {
    isError,
    error,
    startLoading,
    stopLoading,
    progressTo,
    setPaymentInput,
    setLang,
    setError,
  } = useApp()
  
  const {
    omiseConfig,
    responseApiLink,
    responseApiPostCharge,
    responseApiGetCharge,
    setId,
    setResponseApiLink,
    setResponseApiPostCharge,
    setResponseApiGetCharge,
  } = useLink()

  const { track } = useAnalytics()

  const [returnUri, setReturnUri] = useState<string>('');
  const [psp, setPsp] = useState<Psp | null>(null);
  const [paymentFormConfiguration, setPaymentFormConfiguration] = useState<IPaymentFormConfiguration>({
    textForFooter: '',
  });

  const callbackResponseError = (err: AxiosError) => {
    if((err.response?.status || -1) >= 500) {
      setError(ErrorResponse.InternalServer)
    } else {
      setError(ErrorResponse.Unknown)
    }
  }

  const callbackResponseGetLinkError = (err: AxiosError) => {
    if(err.response?.status === 404) {
      setError(ErrorResponse.Unknown)
    } else if(err.response?.status === 409) {
      setError(ErrorResponse.LinkExpiredDate)
    } else {
      callbackResponseError(err)
    }
    progressTo(Progress.Error)
    stopLoading()
  }

  const callbackResponseGetLinkSuccess = (response: AxiosResponse) => {
    const { data } = response || {};
    const transformedData = transformLink(data)
    const isInvalidLink = [
      LinkStatus.Paid,
      LinkStatus.Expired,
    ].includes(transformedData.order.state as LinkStatus)
    const isInvalidPublicKey = !transformedData.merchant.publicKey
    // even response 200, but needs handle errors these cases
    if(isInvalidLink || isInvalidPublicKey) {
      setError(ErrorResponse.LinkExpiredDate)
      setPaymentInput(PaymentInput.FillForm)
      progressTo(Progress.Error)
      stopLoading()
    } else {
      // if user visit paylink first time, set default language as custom from merchant dashboard
      if(!getItem(STORAGE_LANGUAGE)) {
        setLang(transformedData.lang)
      }
      setId(transformedData.id)
      setResponseApiLink(transformedData)
    }

    /*
      TODO:
      * Tracker should be moved to specific views / components.
      * Not possible at this stage due to components rerendering issues.
     */
    const { order, merchant } = transformedData || {};
    const currency = (order.currency || '').toUpperCase()

    track(events.payment_link_opened, {
      link_id: transformedData.id,
      link_name: transformedData.name,
      is_saved_card: transformedData.saveCard,
      amount: order.amount,
      amount_displayed: `${order.currency} ${getPayAmount(order.amount, currency)}`,
      currency,
      lang: transformedData.lang,
      merchant_name: merchant.name
    })
  }

  // send request to server every 1 second for checking payment status
  const onCheckingChargeStatus = (chargeId: string, finite: boolean, clientId?: string) => {
    // finite is false for qr payment as we wait for user to scan and pay behind the scene
    // show qr for users to scan instead of loader
    if (finite) {
      startLoading()
    }

    const linkId = getLinkId(window.location);
    const hasChargeId = chargeId && chargeId !== '';
    const hasClientId = clientId && clientId !== '';

    if (!hasChargeId && !hasClientId) {
      const error = new Error('Charge ID not found.')
      console.warn(error)
      stopLoading()
      setError(ErrorResponse.UnknownStatus)
    }

    let retryCount= RETRY_COUNT

    if (hasChargeId || hasClientId) {
      const loop = setInterval(() => {
        retryCount--;
        const chargeRequest = hasChargeId
          ? getCharges(chargeId)
          : getChargeByClientId(clientId as string, linkId);
        chargeRequest
        .then((response: AxiosResponse) => {
          const responseApi = transformGetCharge(response.data)
          if (response.data?.capturable) {
            clearInterval(loop);
            setError(ErrorResponse.None);
            setResponseApiGetCharge(responseApi);
            progressTo(Progress.Paid);
            stopLoading()
          }
          if(responseApi.status === OmiseChargeStatus.Successful) {
            clearInterval(loop)
            setError(ErrorResponse.None)
            setResponseApiGetCharge(responseApi)
            progressTo(Progress.Paid)
            stopLoading()
          } else if(responseApi.status === OmiseChargeStatus.Failed) {
            clearInterval(loop)
            setError(ErrorResponse.None)
            setResponseApiGetCharge(responseApi)
            progressTo(Progress.PaymentFail)
            stopLoading()
          } else if(responseApi.status !== OmiseChargeStatus.Pending) {
            clearInterval(loop)
            setError(ErrorResponse.Unknown)
            stopLoading()
          }
        })
        .catch((err) => {
          if (err.response.status!==404){
            const linkId = getLinkId(window.location)
            const linkUri = getPaylinkUri(window.location, linkId)
            clearInterval(loop)
            redirect(linkUri, window.location)
          }else{
            if (finite && retryCount===0){
              clearInterval(loop)
              setError(ErrorResponse.UnknownStatus)
              stopLoading()
            }
          }
        })
      }, RETRY_TIME)
    }
  }

  useEffect(() => {
    const callbackType = getCallbackUriType(window.location)
    if(callbackType === CallbackUriPath.Auth) {
      const searchParams = new URLSearchParams(window.location.search)
      const clientId = searchParams.get("client_id") as string
      let chargeId = getItem(STORAGE_CHARGE_ID)

      // if charge ID is not in storage then probably checkout page is active
      // For checkout page, charge ID can be found in the URL as query params
      if (chargeId === '') {
        chargeId = searchParams.get("chargeId") as string
      }
      startLoading()
      const linkId = getLinkId(window.location);
      getLink(linkId)
        .then((response: AxiosResponse) => {
          const companyInfo = response?.data?.company;
          const psp = response?.data?.company?.psp as Psp;
          stopLoading()
          updateFavicon(psp);
          setPsp(psp)
          if (response?.data?.is_locked) {
            setError(ErrorResponse.NoAccess);
          } else if (response?.data?.state === LinkStatus.Disabled) {
            setError(ErrorResponse.LinkDisabled);
          } else {
            setReturnUri(response?.data?.returnUrl || companyInfo?.return_url);
            setPaymentFormConfiguration({
              ...paymentFormConfiguration,
              textForFooter: psp.metadata.data?.payment_form_configuration?.text_for_footer || "",
            });
            onCheckingChargeStatus(chargeId, response?.data?.is_checkout_page_enabled, clientId)
          }
        })
        .catch(callbackResponseGetLinkError)
    } else {
      // clear "charge_id" from web browser every time
      // when payers making a new payment
      removeItem(STORAGE_CHARGE_ID)

      const id = getLinkId(window.location)
      getLink(id)
      .then((response: AxiosResponse) => {
        stopLoading()
        const { data } = response || {}
        const companyInfo = data?.company
        const psp = companyInfo?.psp as Psp;
        updateFavicon(psp);
        // Prioritize return URL from link API response
        setReturnUri(response?.data?.returnUrl || companyInfo?.return_url);
        if (data?.is_locked) {
          setError(ErrorResponse.NoAccess);
        } else if (data?.state === LinkStatus.Disabled) {
          setError(ErrorResponse.LinkDisabled);
        } else if (data?.is_checkout_page_enabled) {
          window.location.href = data.checkout_session_url;
        } else {
          setPsp(psp);
          callbackResponseGetLinkSuccess(response);
          setPaymentFormConfiguration({
            ...paymentFormConfiguration,
            textForFooter: psp.metadata.data?.payment_form_configuration?.text_for_footer || "",
          });
        }
      })
      .catch(callbackResponseGetLinkError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = (payload: OmiseCallbackPayload) => {
    setError(ErrorResponse.None)
    startLoading()
    const data = getChargeBody(payload)
    postCharges(data)
      .then((response: AxiosResponse) => {
        const responseApi = transformPostCharge(response.data)
        const isScanQrCode = !!responseApi.qrCodeUri
        const isRequiredAuth = !!responseApi.authorizeUri
        const chargeId = responseApi.id
        const { order } = responseApiLink
        track(events.payment_created, {
          link_id: responseApiLink.id,
          link_name: responseApiLink.name,
          is_saved_card: responseApiLink.saveCard,
          payment_method: responseApi.paymentMethod,
        })
        if(isScanQrCode) {
          setResponseApiPostCharge(responseApi)
          setPaymentInput(PaymentInput.ScanQrCode)
          progressTo(Progress.Checkout)
          stopLoading()

          onCheckingChargeStatus(chargeId, false, data.client_id)
        } else if(isRequiredAuth) {
          // set "charge_id" on web browser, will be used when
          // payer redirect from 3rd service to see result on link+ app
          setItem(STORAGE_CHARGE_ID, responseApi.id)

          // stop loading before redirect so the tab-close guard doesn't
          // prompt the native "leave site?" dialog on our own navigation
          stopLoading()
          redirect(responseApi.authorizeUri, window.location)
        } else {
          setResponseApiPostCharge(responseApi)
          progressTo(Progress.Paid)
          stopLoading()
        }
        track(events.payment_captured, {
          link_id: responseApiLink.id,
          link_name: responseApiLink.name,
          is_saved_card: responseApiLink.saveCard,
          payment_method: responseApi.paymentMethod,
          amount: order.amount,
          currency: (order.currency || "").toUpperCase(),
        })
        if (responseApiLink.saveCard) {
          track(events.card_saved, {
            link_id: responseApiLink.id,
            link_name: responseApiLink.name,
            merchant_name: responseApiLink.merchant.name,
            psp: psp?.identifier ?? "",
          })
        }
      })
      .catch(() => {
        setError(ErrorResponse.PaymentFail)
        stopLoading()
      })
  }

  const handleSendReceiptEmail = (email: string, callback: (error: any) => void) => {
    setError(ErrorResponse.None)
    const data = getEmailReceiptBody(email)
    postEmailReceipt(responseApiGetCharge.id, data)
      .then(() => {
        const err = null
        callback(err)
      })
      .catch((err: AxiosError) => {
        setError(ErrorResponse.SentEmailFail)
        callback(err)
      })
  }

  const handleCancelSendReceiptEmail = () => {
    setError(ErrorResponse.None)
  }

  const handleClickBackQrCode = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    progressTo(Progress.Checkout)
    setPaymentInput(PaymentInput.FillForm)
    setError(ErrorResponse.PaymentCancel)
  }

  const handleClickBackToMerchantPage = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    redirect(returnUri, window.location);
  }

  return (
    <>
      { isError ?
        <InvalidLink
          retry={()=>{
            setError(ErrorResponse.None)
            const searchParams = new URLSearchParams(window.location.search)
            onCheckingChargeStatus(
              searchParams.get("chargeId") as string,
              true,
              searchParams.get("client_id") as string,
            )
          }}
          type={error}
          paymentFormConfiguration={paymentFormConfiguration}
        /> :
        <Paylink
          omiseConfig={omiseConfig}
          responseApiPostCharge={responseApiPostCharge}
          responseApiGetCharge={responseApiGetCharge}
          error={error}
          onReady={() => stopLoading()}
          onSubmit={handleSubmit}
          onSendReceiptEmail={handleSendReceiptEmail}
          onCancelSendReceiptEmail={handleCancelSendReceiptEmail}
          onClickBackQrCode={handleClickBackQrCode}
          onClickBackToMerchantPage={returnUri ? handleClickBackToMerchantPage : null}
          paymentFormConfiguration={paymentFormConfiguration}
          psp={psp}
        />
      }
    </>
  )
}

export default Index
